import { getStyle } from "@/components/dragndrop/dndStyle";
import { DndWrapper } from "@/components/dragndrop/dndWrapper";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "@/components/dragndrop/formEditorStyles.css";
import { LeftColumnWrapper } from "@/components/dragndrop/dndLeftColumnWrapper";
import { Tooltip } from "@mui/material";
import { DnDCollapsible } from "@/components/dragndrop/dndCollapsible";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import styles from "@/components/dragndrop/dndItem.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";

interface DNDStyle {
  height?: number;
  left?: number;
  position?: string;
  right?: number;
  top?: number;
  width?: string;
  borderRadius?: string;
  border?: string;
  alignItems?: string;
  justifyContent?: string;
}
export interface DNDItem {
  conditions?: any;
  created_on?: string;
  created_by?: string;
  id: string;
  label: string;
  modified_on?: string;
  modified_by?: string;
  name: string;
  next_version?: number;
  reuse?: boolean;
  sample_data?: any;
  trace_id?: string;
  type?: string;
  unit?: string;
  value_map?: any;
  version?: number;
  mandatory?: boolean;
  visibility_ruleset?: any;
  observation?: any;
  assessment?: any;
  dangerous_answer?: any;
  possible_remedy?: any;
  danger_potential?: any;
}
interface Props {
  provided?: any;
  item: DNDItem | any;
  style?: DNDStyle | string;
  isDragging?: boolean;
  columnId?: string;
  handleDeleteItem?: (id: string | undefined) => void;
  onHandleCheckboxChange?: (id: string) => void;
  onHandleRulesetChange?: (event?: any, id?: any) => void;

  preExistingFormElements?: any;
  index?: number;
  itemCount?: number;
  onMoveItem?: (index: number, direction: 1 | -1) => void;
  showFieldPreview?: boolean;
  keySuggestions?: { value: string; label?: string }[];
  forceSettingsExpanded?: boolean;
  rulesetValidationTargetRef?: React.RefCallback<HTMLElement>;
}

export default function Item({
  provided,
  item,
  style,
  isDragging,
  columnId,
  handleDeleteItem,
  preExistingFormElements,
  onHandleCheckboxChange,
  onHandleRulesetChange,
  index,
  itemCount,
  onMoveItem,
  showFieldPreview = true,
  keySuggestions,
  forceSettingsExpanded,
  rulesetValidationTargetRef,
}: Props) {
  const { t } = useTranslation();

  function renderWarningSign() {
    const propertiesToCheck = [
      "observation",
      "assessment",
      "dangerous_answer",
      "possible_remedy",
      "danger_potential",
    ];
    let warning = false;
    for (const property of propertiesToCheck) {
      if (
        item[property] === null ||
        item[property] === undefined ||
        item[property] === ""
      ) {
        warning = true;
      }
    }

    if (warning) {
      return (
        <Tooltip
          arrow
          placement="top"
          title={t("formeditor_variable_not_usable")}
        >
          <WarningAmberIcon color="warning" fontSize="small" />
        </Tooltip>
      );
    }
  }

  function renderVisibilityIndicator() {
    if (
      item?.visibility_ruleset &&
      (typeof item.visibility_ruleset === "string"
        ? item.visibility_ruleset.trim() !== "{}"
        : JSON.stringify(item.visibility_ruleset) !== "{}")
    ) {
      return (
        <Tooltip
          arrow
          placement="top"
          title={t("formeditor_visibility_ruleset_present")}
        >
          <VisibilityIcon color="success" fontSize="small" />
        </Tooltip>
      );
    }
    return null;
  }

  const showMoveButtons =
    typeof index === "number" &&
    typeof itemCount === "number" &&
    onMoveItem !== undefined;

  return (
    <div
      {...provided.draggableProps}
      {...(columnId === "column-0" && provided.dragHandleProps)}
      ref={provided.innerRef}
      style={getStyle({
        draggableStyle: provided.draggableProps.style,
        virtualStyle: style,
        isDragging,
      })}
      className={`item ${columnId === "column-1" ? "item--card" : ""} ${
        isDragging ? "is-dragging" : ""
      }`}
    >
      {columnId === "column-1" && (
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft} {...provided.dragHandleProps}>
            <DragHandleIcon className={styles.dragIcon} />
            {typeof index === "number" && (
              <span className={styles.positionBadge}>{index + 1}</span>
            )}
            <div className={styles.titleBlock}>
              <span className={styles.cardLabel} title={item?.label}>
                {item?.label}
              </span>
              <span className={styles.cardName} title={item?.name}>
                {item?.name}
              </span>
            </div>
          </div>
          <div className={styles.cardHeaderRight}>
            {item?.type && (
              <span className={styles.typeChip}>{t(item.type)}</span>
            )}
            <span className={styles.statusIcons}>
              {renderWarningSign()}
              {renderVisibilityIndicator()}
            </span>
            {showMoveButtons && (
              <span className={styles.moveButtons}>
                <Tooltip arrow placement="top" title={t("move_up")}>
                  <span>
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      aria-label="move-up"
                      disabled={index === 0}
                      onClick={() => onMoveItem?.(index as number, -1)}
                    >
                      <KeyboardArrowUpIcon />
                    </GenericButton>
                  </span>
                </Tooltip>
                <Tooltip arrow placement="top" title={t("move_down")}>
                  <span>
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      aria-label="move-down"
                      disabled={index === (itemCount as number) - 1}
                      onClick={() => onMoveItem?.(index as number, 1)}
                    >
                      <KeyboardArrowDownIcon />
                    </GenericButton>
                  </span>
                </Tooltip>
              </span>
            )}
            <Tooltip
              arrow
              placement="left"
              title={t("formeditor_measurementdefinition_edit", {
                name: item?.label,
              })}
            >
              <Link
                to={"/settings/createMeasurementDefinition/" + item?.id}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <GenericButton variant="icon" color="iconBase">
                  <EditIcon />
                </GenericButton>
              </Link>
            </Tooltip>
            <Tooltip arrow placement="left" title={t("formeditor_copy_name")}>
              <GenericButton
                variant="icon"
                color="iconBase"
                aria-label="copy"
                onClick={() => {
                  navigator.clipboard.writeText(item?.name);
                }}
              >
                <ContentCopyIcon />
              </GenericButton>
            </Tooltip>
            <Tooltip arrow placement="right" title={t("delete")}>
              <GenericButton
                variant="icon"
                color="error"
                type="button"
                aria-label="delete"
                onClick={() => handleDeleteItem?.(item?.id)}
              >
                <DeleteIcon />
              </GenericButton>
            </Tooltip>
          </div>
        </div>
      )}
      {columnId === "column-0" && (
        <LeftColumnWrapper
          label={item?.label}
          type={item?.type}
          name={item?.name}
        />
      )}
      {columnId === "column-1" && (
        <>
          {showFieldPreview && (
            <div className={styles.previewSection}>
              <DndWrapper
                columnId={columnId}
                type={item?.type}
                name={item?.name}
                label={item?.label}
                rows={2}
                cols={55}
                id={item?.id}
                style={style}
                item={item}
                mandatory={item?.mandatory}
                visibility_ruleset={item?.visibility_ruleset}
                preExistingFormElements={preExistingFormElements}
                handleDeleteItem={handleDeleteItem}
                onHandleCheckboxChange={onHandleCheckboxChange}
              />
            </div>
          )}
          <DnDCollapsible
            visibility_ruleset={item?.visibility_ruleset}
            mandatory={item?.mandatory}
            onHandleCheckboxChange={() => onHandleCheckboxChange?.(item.id)}
            onHandleRulesetChange={onHandleRulesetChange}
            id={item.id}
            keySuggestions={keySuggestions?.filter(
              (suggestion) => suggestion.value !== item?.name,
            )}
            forceExpanded={forceSettingsExpanded}
            validationTargetRef={rulesetValidationTargetRef}
          />
        </>
      )}
    </div>
  );
}
